/**
 * <VoiceBars> — il parle, en trois petites barres vertes (atom).
 *
 * Sur une vignette du live, à droite du prénom. Une seule entrée, oui ou non (`speaking`) : l'écran la lit sur
 * l'événement « parle / se tait » de LiveKit, le même que le liseré vert de la vignette. Aucune lecture du niveau
 * de la voix : les barres ne mesurent rien, elles disent qu'il parle.
 *
 * Il parle : les barres ondulent, une boucle que joue la plateforme seule, sans un seul rendu de React tant qu'elles
 * bougent. Au natif, une seule phase par rangée tourne au pilote natif (une `Animated.timing` en boucle, sans aller-
 * retour par le fil JS), et chaque barre la lit décalée d'un tiers. Au web, une animation CSS posée par un attribut
 * (`data-voice`) : le style de la barre ne change jamais, Unistyles ne réécrit pas sa feuille à chaque prise de
 * parole. Il se tait : elles s'arrêtent tout de suite, au repos. En mouvement réduit : un point vert fixe pendant
 * qu'il parle, rien sinon.
 */
import React, { useEffect, useMemo, useRef } from "react"
import { Animated, Easing, Platform, View, type ViewProps } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { useReduceMotion } from "../../hooks/useReduceMotion"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { voiceWave } from "../../theme/tokens/motion"
import { injectStyleOnce } from "../../utils/injectStyleOnce"

export type VoiceBarsProps = {
    /** Il parle : les barres ondulent. Défaut `false`, au repos. */
    speaking?: boolean
    /** La hauteur des barres, en px. Défaut 12, celle d'un prénom de vignette. */
    size?: number
    /** Défaut `success`, le vert de « il parle ». */
    color?: SemanticColorKey
    testID?: string
}

/** La barre au repos, en part de sa hauteur : assez haute pour se voir, basse pour ne rien dire. */
const REST = 0.25
const BAR_WIDTH = 3
const BARS = [0, 1, 2] as const
/** Chaque barre part un tiers de vague après sa voisine : l'onde passe de l'une à l'autre. */
const shiftOf = (index: number) => index / BARS.length

/** La hauteur d'une barre à une phase de la vague (0 → 1) : du repos au sommet à mi-vague, puis retour. */
const waveAt = (phase: number) => {
    const x = phase - Math.floor(phase)
    return REST + (1 - REST) * (1 - Math.abs(2 * x - 1))
}
/** La vague en six points : les sommets et les creux de chaque barre tombent dessus, l'interpolation est exacte. */
const PHASES = [0, 1, 2, 3, 4, 5, 6].map((step) => step / 6)

const WEB_WAVE = "masteur-voice-wave"
const WEB_WAVE_CSS = `@keyframes ${WEB_WAVE} {
  0%, 100% { transform: scaleY(${REST}); }
  50% { transform: scaleY(1); }
}
[data-voice-bar] { transform: scaleY(${REST}); }
[data-voice="on"] [data-voice-bar] {
  animation: ${WEB_WAVE} ${voiceWave.period}ms ease-in-out infinite;
}
${BARS.map(
    (index) =>
        `[data-voice="on"] [data-voice-bar="${index}"] { animation-delay: ${-(1 - shiftOf(index)) * voiceWave.period}ms; }`
).join("\n")}`

/** `dataSet` : react-native-web le rend en attributs `data-*` ; les types de React Native ne le connaissent pas. */
const webData = (dataSet: Record<string, string | number>) => ({ dataSet }) as Partial<ViewProps>

export function VoiceBars(props: VoiceBarsProps) {
    const { speaking = false, size = 12, color = "success", testID } = props
    const calm = useReduceMotion()
    const web = Platform.OS === "web"
    const scales = useNativeWave(!web && speaking && !calm)
    useEffect(() => {
        if (web) injectStyleOnce(WEB_WAVE, WEB_WAVE_CSS)
    }, [web])

    return (
        <View
            style={styles.row(size)}
            testID={testID}
            pointerEvents="none"
            accessibilityElementsHidden
            {...(web && webData({ voice: speaking && !calm ? "on" : "off" }))}
        >
            {calm
                ? speaking && <View style={styles.dot(color, size)} />
                : BARS.map((index) => (
                      <View key={index} style={styles.slot}>
                          {web ? (
                              <View style={styles.fill(color)} {...webData({ voiceBar: index })} />
                          ) : (
                              <Animated.View
                                  style={[styles.native, { transform: [{ scaleY: speaking ? scales[index] : REST }] }]}
                              >
                                  <View style={styles.fill(color)} />
                              </Animated.View>
                          )}
                      </View>
                  ))}
        </View>
    )
}

/**
 * Au natif, la phase de la vague et la hauteur de chaque barre qui la lit. La boucle tourne tant qu'il parle, et
 * s'arrête dès qu'il se tait. Au web, elle ne tourne pas : l'animation est en CSS.
 */
function useNativeWave(running: boolean) {
    const phase = useRef(new Animated.Value(0)).current
    const scales = useMemo(
        () =>
            BARS.map((index) =>
                phase.interpolate({
                    inputRange: PHASES,
                    outputRange: PHASES.map((at) => waveAt(at - shiftOf(index))),
                })
            ),
        [phase]
    )
    useEffect(() => {
        if (!running) return undefined
        phase.setValue(0)
        const loop = Animated.loop(
            Animated.timing(phase, {
                toValue: 1,
                duration: voiceWave.period,
                easing: Easing.linear,
                useNativeDriver: true,
            })
        )
        loop.start()
        return () => loop.stop()
    }, [running, phase])
    return scales
}

const styles = StyleSheet.create((theme) => ({
    row: (size: number) => ({
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: 2,
        height: size,
        minWidth: BAR_WIDTH * BARS.length + 4,
    }),
    slot: {
        width: BAR_WIDTH,
        height: "100%",
    },
    native: { flex: 1 },
    fill: (color: SemanticColorKey) => ({
        flex: 1,
        borderRadius: BAR_WIDTH / 2,
        backgroundColor: theme.colors[color],
    }),
    dot: (color: SemanticColorKey, size: number) => ({
        width: Math.round(size / 2),
        height: Math.round(size / 2),
        borderRadius: size / 4,
        backgroundColor: theme.colors[color],
    }),
}))
